import { useQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import { investmentAum } from "$root/functional-areas/customAttributes/aum";
import type { ReviewEntity } from "$root/pages/ReviewInstruments.const";
import { useMappedQueries } from "$root/third-party-integrations/react-query/hooks";
import { ReviewHeader } from "../ReviewHeader";
import { useReviewCompositionQueries } from "../reviewQueries";
import { AumUnavailableDialog } from "./AumUnavailableDialog";
import { sizeNewAlias } from "./newAlias";
import { QuantityReview, type QuantityReviewProps, type SizeAlias } from "./QuantityReview";
import { toQuantityReviewRows } from "./rows";

export type QuantityReviewContainerProps = Omit<QuantityReviewProps, "rows" | "aum" | "sizeAlias"> & {
	uuid: string;
	entity: ReviewEntity;
	/** Where the AUM-unavailable dialog leads: the review cannot be shown without the AUM. */
	onLeave(): void;
};

/**
 * Loads the rows of a review in quantities, priced against the portfolio's AUM; until they arrive the
 * header stands alone, its Save disabled.
 */
export function QuantityReviewContainer({
	uuid,
	entity,
	onLeave,
	...view
}: QuantityReviewContainerProps): React.JSX.Element {
	const { composition, userInstruments } = useReviewCompositionQueries(uuid, entity);
	const queryAum = useQuery(investmentAum.getQueryConfig(uuid, "INVESTMENT"));
	const queryRows = useMappedQueries(
		([compositionEditor, userInstrumentsMap, aum]) =>
			toQuantityReviewRows({ response: compositionEditor, userInstruments: userInstrumentsMap, aum }),
		[],
		[composition, userInstruments, queryAum],
	);
	const sizeAlias = useCallback<SizeAlias>((params) => sizeNewAlias({ investmentUuid: uuid, ...params }), [uuid]);

	return (
		<>
			{queryRows.data == null && (
				<ReviewHeader {...view.header} save={view.canEditComposition ? { kind: "loading" } : undefined} />
			)}
			<ReactQueryWrapperBase query={queryRows}>
				{({ rows, aum }) => <QuantityReview {...view} rows={rows} aum={aum} sizeAlias={sizeAlias} />}
			</ReactQueryWrapperBase>
			<AumUnavailableDialog show={queryAum.isError} onLeave={onLeave} />
		</>
	);
}
